import React, { useState, useMemo } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { useUserProperties } from '../hooks/useUserProperties';
import TagsInput from '../../ui/TagsInput';
import { deriveAvailableTags } from '../../../utils/tags';

/**
 * Mobile modal for adding a new lead.
 * @param {Object} props
 * @param {function} props.onClose - Close modal callback
 * @param {function} props.onAddLead - Add lead callback
 * @param {Array} props.leads - All leads for extracting existing tags
 */
export const MobileAddLeadModal = ({ onClose, onAddLead, leads = [] }) => {
    const { properties, loading: propertiesLoading } = useUserProperties();

    // Derive unique normalized tags from all existing leads
    const availableTags = useMemo(() => deriveAvailableTags(leads), [leads]);

    const [selectedTags, setSelectedTags] = useState([]);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        status: 'Active',
        interestedPropertyIds: [], // Multiple property IDs
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleToggleProperty = (propertyId) => {
        setFormData((prev) => {
            const ids = prev.interestedPropertyIds;
            if (ids.includes(propertyId)) {
                return { ...prev, interestedPropertyIds: ids.filter(id => id !== propertyId) };
            }
            return { ...prev, interestedPropertyIds: [...ids, propertyId] };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Build interested properties array with id and address
        const interestedProperties = formData.interestedPropertyIds
            .map(id => {
                const prop = properties.find(p => p.id === id);
                return prop ? { id: prop.id, address: prop.address || prop.displayName } : null;
            })
            .filter(Boolean);

        // Pass firstName and lastName separately - service will generate fullName
        if (onAddLead) onAddLead({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            address: formData.address,
            status: formData.status,
            interestedProperties,
            tags: selectedTags,
        });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-stone-900/30 backdrop-blur-sm z-[100] flex items-center justify-center p-4 safe-area-inset">
            <div className="bg-white w-full max-w-sm max-h-[calc(100vh_-_env(safe-area-inset-bottom)_-_32px)] rounded-t-3xl shadow-sheet flex flex-col fade-in">
                {/* Header - Fixed */}
                <div className="px-6 py-4 border-b border-stone-100 flex-shrink-0">
                    <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-lg text-notion-text">Add New Lead</h3>
                        <button onClick={onClose} className="p-2 bg-stone-100 rounded-full text-stone-500 hover:bg-stone-200 transition">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Form - Scrollable with padding for sticky footer */}
                <div className="flex-1 overflow-y-auto px-6 py-4 pb-20">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* First Name */}
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            placeholder="First Name"
                            className="w-full px-4 py-3 bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded-xl text-sm outline-none transition"
                            required
                        />

                        {/* Last Name */}
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder="Last Name"
                            className="w-full px-4 py-3 bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded-xl text-sm outline-none transition"
                            required
                        />

                        {/* Email */}
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Email Address"
                            className="w-full px-4 py-3 bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded-xl text-sm outline-none transition"
                            required
                        />

                        {/* Phone */}
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="Phone Number"
                            className="w-full px-4 py-3 bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded-xl text-sm outline-none transition"
                            required
                        />

                        {/* Address */}
                        <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            placeholder="Address (Optional)"
                            className="w-full px-4 py-3 bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded-xl text-sm outline-none transition"
                        />

                        {/* Interested Properties */}
                        <div>
                            <label className="block text-[10px] font-medium text-stone-500 mb-2">Interested Properties (Optional)</label>
                            <div className="max-h-36 overflow-y-auto border border-stone-200 rounded-xl bg-notion-sidebar divide-y divide-stone-100">
                                {propertiesLoading ? (
                                    <div className="p-3 text-sm text-stone-400">Loading...</div>
                                ) : properties.length === 0 ? (
                                    <div className="p-3 text-sm text-stone-400 italic">No properties available</div>
                                ) : (
                                    properties.map((property) => (
                                        <label
                                            key={property.id}
                                            className="flex items-center gap-3 px-4 py-3 hover:bg-white cursor-pointer transition"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={formData.interestedPropertyIds.includes(property.id)}
                                                onChange={() => handleToggleProperty(property.id)}
                                                className="w-5 h-5 rounded border-stone-300 text-stone-800 focus:ring-stone-500"
                                            />
                                            <span className="text-sm text-stone-700">{property.displayName}</span>
                                        </label>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Tags */}
                        <div>
                            <label className="block text-[10px] font-medium text-stone-500 mb-2">Tags (Optional)</label>
                            <TagsInput
                                tags={selectedTags}
                                onChange={setSelectedTags}
                                suggestions={availableTags}
                                placeholder="Add tags..."
                                size="md"
                            />
                        </div>

                        {/* Status */}
                        <div>
                            <label className="block text-[10px] font-medium text-stone-500 mb-2">Status</label>
                            <div className="relative">
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded-xl text-sm outline-none appearance-none text-stone-600"
                                >
                                    <option>Active</option>
                                    <option>Closed</option>
                                    <option>Archived</option>
                                    <option>Cold</option>
                                </select>
                                <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-stone-400 pointer-events-none" />
                            </div>
                        </div>
                    </form>
                </div>

                {/* Footer - Sticky */}
                <div className="sticky bottom-0 px-6 py-4 border-t border-stone-100 bg-white flex-shrink-0">
                    <button type="submit" className="w-full py-3.5 bg-stone-800 text-white text-sm font-semibold rounded-xl hover:bg-stone-700 transition shadow-lg" onClick={handleSubmit}>
                        Create Lead
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MobileAddLeadModal;

